// Due / overdue check reminders: turning the rows a provider returns into the groups and labels a
// dashboard cell draws (IM-027, sprint 7).
//
// Framework-free on purpose. Everything worth arguing about — which group a record falls into, how a
// date is worded, what a record belonging to no project is called — is here, where it can be tested
// without a dashboard, a DOM or a server.
//
// ⚠️ THE CLASSIFICATION ITSELF IS NOT REPEATED HERE. `Urgency` and `DaysUntilDue` arrive on the row,
// computed once server-side by `CheckReminderClassification`. That is deliberate: "overdue" is a fact
// about two dates, not a reading of the data, and a second implementation in the browser would
// eventually disagree with the first — the browser's clock is the user's, and a laptop an hour behind
// midnight in another time zone would classify the same record differently from the server that
// filtered it. This file words those values; it never recomputes them.

/** One row as the provider returns it. Property names are PascalCase — the wire keeps C# casing. */
export interface CheckReminderRow {
    RecordId: number;
    Title?: string | null;
    SubTypeName?: string | null;
    ProjectId?: number | null;
    ProjectNumber?: string | null;
    ProjectName?: string | null;
    ResponsibleContactId?: number | null;
    ResponsibleContactName?: string | null;
    DueDate?: string | null;
    DaysUntilDue?: number | null;
    Urgency?: string | null;
    Status?: string | null;
    PeriodKey?: string | null;
}

/** Mirrors `KeyView.Compliance.CheckReminderWidgetConfig`. */
export interface CheckReminderConfig {
    Title?: string | null;
    ShowProject?: boolean | null;
    ShowResponsible?: boolean | null;
    GroupByUrgency?: boolean | null;
    EmptyText?: string | null;
}

/** Mirrors `KeyView.Compliance.CheckReminderUrgency`. */
export const Overdue = "Overdue";
export const DueToday = "DueToday";
export const DueSoon = "DueSoon";

export type UrgencyKey = typeof Overdue | typeof DueToday | typeof DueSoon;

/** What a record belonging to no project is called. Mirrors `CheckReminderWidgetConfig.NoProjectLabel`. */
export const NoProjectLabel = "No project";

export const DefaultEmptyText = "Nothing due";

/** One heading and the records under it. */
export interface CheckReminderGroup {
    key: UrgencyKey;
    label: string;
    rows: CheckReminderRow[];
}

/** What the widget needs to draw one cell. */
export interface CheckReminderView {
    groups: CheckReminderGroup[];
    total: number;
    overdue: number;
}

/**
 * Most urgent first, and the order is the whole point of the grouping: a reminder list that put
 * what is merely approaching above what is already late would be reporting the wrong thing calmly.
 */
const GroupOrder: UrgencyKey[] = [Overdue, DueToday, DueSoon];

const GroupLabels: Record<UrgencyKey, string> = {
    [Overdue]: "Overdue",
    [DueToday]: "Due today",
    [DueSoon]: "Due soon",
};

/**
 * The urgency of a row, as a key this module recognises.
 *
 * ⚠️ An unrecognised or missing value falls to `DueSoon`, never to `Overdue`. A row the browser
 * cannot classify is a row about which nothing is known, and a widget that guessed "overdue" would
 * put a record on somebody's late list on the strength of a value it failed to read.
 */
export function urgencyOf(row: CheckReminderRow): UrgencyKey {
    const value = row?.Urgency;
    return value === Overdue || value === DueToday ? value : DueSoon;
}

/**
 * Whether a row carries a usable record id. A C# long arrives as a number today, but from Serenity 10.6 one
 * beyond JavaScript's safe-integer range is serialised as a string, so a whole-number string counts too.
 * Only the presence of an id is judged here; it is passed on as received, never computed with.
 */
export function isRecordId(value: unknown): boolean {
    return (typeof value === "number" && Number.isInteger(value))
        || (typeof value === "string" && /^\d+$/.test(value));
}

/** The host hands over whatever the provider returned; only an array of rows is usable. */
export function toRows(data: unknown): CheckReminderRow[] {
    return Array.isArray(data) ? (data as CheckReminderRow[]) : [];
}

/**
 * Groups the rows for display, preserving the server's ordering within each group — the provider
 * already returns them most overdue first, and re-sorting here would be a second opinion about the
 * same dates.
 *
 * With `GroupByUrgency` off, everything lands in one group whose heading the widget does not draw,
 * so the list stays in pure due-date order while each line keeps its own urgency styling.
 */
export function buildReminderView(rows: CheckReminderRow[], config?: CheckReminderConfig | null): CheckReminderView {
    const usable = (rows ?? []).filter(r => r && isRecordId(r.RecordId));
    const overdue = usable.filter(r => urgencyOf(r) === Overdue).length;

    if (config?.GroupByUrgency === false) {
        return {
            groups: usable.length === 0 ? [] : [{ key: DueSoon, label: "", rows: usable }],
            total: usable.length,
            overdue,
        };
    }

    const groups = GroupOrder
        .map(key => ({ key, label: GroupLabels[key], rows: usable.filter(r => urgencyOf(r) === key) }))
        .filter(group => group.rows.length > 0);

    return { groups, total: usable.length, overdue };
}

/**
 * How a row's timing is worded.
 *
 * ⚠️ Read the boundary: `DaysUntilDue === 0` is "Due today", not "1 day late" and not "due in 0
 * days". The server draws the same line at the same place — see `CheckReminderClassification` — and
 * this is the wording of that decision, not a second one.
 */
export function dueLabel(row: CheckReminderRow): string {
    const days = row?.DaysUntilDue;

    if (typeof days !== "number" || !Number.isFinite(days))
        return "";

    if (days === 0) return "Due today";
    if (days < 0) {
        const late = Math.abs(days);
        return late === 1 ? "1 day late" : `${late} days late`;
    }

    return days === 1 ? "Due tomorrow" : `Due in ${days} days`;
}

/**
 * What project a record belongs to.
 *
 * ⚠️⚠️ A record with no project renders as `NoProjectLabel`, NEVER as a blank. Overhead and building
 * maintenance are checked and reported on exactly like project work, so "no project" is an ordinary
 * and deliberate state here — a blank in that column reads as missing data and invites somebody to
 * go and "fix" a row that is already correct.
 */
export function projectLabel(row: CheckReminderRow): string {
    if (row?.ProjectId === null || row?.ProjectId === undefined)
        return NoProjectLabel;

    const number = (row.ProjectNumber ?? "").trim();
    const name = (row.ProjectName ?? "").trim();

    if (number && name) return `${number} — ${name}`;

    // ⚠️ A project id with nothing readable behind it is NOT the same as no project, and must not be
    // worded as one — that would report a data-shape problem as a deliberate state and hide it. The
    // id is drawn instead, which is ugly on purpose: it is visible, and it is traceable.
    return number || name || `Project ${row.ProjectId}`;
}

/** The record's own line: its title, with its sub-type when there is one. */
export function titleLabel(row: CheckReminderRow): string {
    const title = (row?.Title ?? "").trim();
    const subType = (row?.SubTypeName ?? "").trim();

    if (title && subType) return `${title} (${subType})`;
    return title || subType || "Untitled check";
}

export function emptyText(config?: CheckReminderConfig | null): string {
    const text = (config?.EmptyText ?? "").trim();
    return text || DefaultEmptyText;
}
